import React, { useState, useEffect, useRef } from "react";
import "./AdminProfileDropdown.css";

/* ─────────────────────────────────
   Helpers
   ───────────────────────────────── */
function authHeader() {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function apiFetch(path, options = {}) {
  const res = await fetch(path, {
    ...options,
    headers: { ...authHeader(), ...(options.headers || {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `HTTP ${res.status}`);
  }
  return res.json();
}

/* ═══════════════════════════════════
   PROFILE MODAL
   ═══════════════════════════════════ */
function ProfileModal({ user, onClose, onLogout }) {
  const [tab, setTab] = useState("profile"); // "profile" | "password"

  /* Profile fields */
  const [profileForm, setProfileForm] = useState({
    username: user?.username || "",
    email:    user?.email    || "",
  });
  const [profileSaving,  setProfileSaving]  = useState(false);
  const [profileSuccess, setProfileSuccess] = useState("");
  const [profileError,   setProfileError]   = useState("");

  /* Password fields */
  const [pwForm, setPwForm] = useState({
    current_password: "",
    new_password:     "",
    confirm_password: "",
  });
  const [pwSaving,  setPwSaving]  = useState(false);
  const [pwSuccess, setPwSuccess] = useState("");
  const [pwError,   setPwError]   = useState("");
  const [showPw, setShowPw] = useState({
    current: false, new: false, confirm: false,
  });

  /* Close on Escape */
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const setProfile = (key, val) =>
    setProfileForm((f) => ({ ...f, [key]: val }));
  const setPw = (key, val) =>
    setPwForm((f) => ({ ...f, [key]: val }));

  /* ── Save profile ── */
  const handleProfileSave = async () => {
    setProfileError("");
    setProfileSuccess("");
    if (!profileForm.username.trim())
      return setProfileError("Username is required.");

    setProfileSaving(true);
    try {
      const userId = user?.id;
      await apiFetch(`/api/admin/users/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: profileForm.username.trim(),
          email:    profileForm.email.trim() || null,
          role:     user?.role || "admin",
          active:   1,
        }),
      });

      /* Update local storage so the topbar refreshes */
      const stored = JSON.parse(localStorage.getItem("admin_user") || "{}");
      stored.username = profileForm.username.trim();
      stored.email    = profileForm.email.trim() || stored.email;
      localStorage.setItem("admin_user", JSON.stringify(stored));

      setProfileSuccess("Profile updated successfully.");
    } catch (err) {
      setProfileError(err.message);
    } finally {
      setProfileSaving(false);
    }
  };

  /* ── Change password ── */
  const handlePasswordSave = async () => {
    setPwError("");
    setPwSuccess("");
    if (!pwForm.current_password) return setPwError("Current password is required.");
    if (!pwForm.new_password)     return setPwError("New password is required.");
    if (pwForm.new_password.length < 8)
      return setPwError("New password must be at least 8 characters.");
    if (pwForm.new_password !== pwForm.confirm_password)
      return setPwError("Passwords do not match.");

    setPwSaving(true);
    try {
      await apiFetch(`/api/admin/users/${user?.id}/change-password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          current_password: pwForm.current_password,
          new_password:     pwForm.new_password,
        }),
      });
      setPwSuccess("Password changed successfully.");
      setPwForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      setPwError(err.message);
    } finally {
      setPwSaving(false);
    }
  };

  const initials = (user?.username || "AD").slice(0, 2).toUpperCase();

  const togglePw = (key) =>
    setShowPw((v) => ({ ...v, [key]: !v[key] }));

  return (
    <div className="apd-modal-overlay" onClick={onClose}>
      <div className="apd-modal" onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="apd-modal__header">
          <div className="apd-modal__avatar-row">
            <div className="apd-modal__avatar">{initials}</div>
            <div className="apd-modal__user-meta">
              <span className="apd-modal__username">{user?.username || "Admin"}</span>
              <span className="apd-modal__role">{user?.role || "administrator"}</span>
              {user?.email && (
                <span className="apd-modal__email">{user.email}</span>
              )}
            </div>
          </div>
          <button className="apd-modal__close" onClick={onClose} title="Close">✕</button>
        </div>

        {/* ── Tabs ── */}
        <div className="apd-modal__tabs">
          <button
            className={`apd-tab ${tab === "profile" ? "apd-tab--active" : ""}`}
            onClick={() => { setTab("profile"); setProfileError(""); setProfileSuccess(""); }}
          >
            <span className="apd-tab__icon">◈</span> Profile
          </button>
          <button
            className={`apd-tab ${tab === "password" ? "apd-tab--active" : ""}`}
            onClick={() => { setTab("password"); setPwError(""); setPwSuccess(""); }}
          >
            <span className="apd-tab__icon">🔒</span> Password
          </button>
        </div>

        {/* ── Body ── */}
        <div className="apd-modal__body">

          {/* PROFILE TAB */}
          {tab === "profile" && (
            <div className="apd-form">
              <div className="apd-form__section-title">Account Details</div>

              {profileError   && <div className="apd-alert apd-alert--error">⚠ {profileError}</div>}
              {profileSuccess && <div className="apd-alert apd-alert--success">✓ {profileSuccess}</div>}

              <div className="apd-form__field">
                <label className="apd-form__label">
                  Username <span className="apd-req">*</span>
                </label>
                <input
                  className="apd-form__input"
                  type="text"
                  value={profileForm.username}
                  onChange={(e) => setProfile("username", e.target.value)}
                  placeholder="e.g. john_admin"
                />
              </div>

              <div className="apd-form__field">
                <label className="apd-form__label">Email Address</label>
                <input
                  className="apd-form__input"
                  type="email"
                  value={profileForm.email}
                  onChange={(e) => setProfile("email", e.target.value)}
                  placeholder="e.g. admin@company.co.za"
                />
                <p className="apd-form__hint">Used for notifications and recovery.</p>
              </div>

              <div className="apd-form__field apd-form__field--readonly">
                <label className="apd-form__label">Role</label>
                <div className="apd-form__readonly">
                  <span className="apd-role-badge">{user?.role || "admin"}</span>
                  <span className="apd-form__hint" style={{ marginLeft: 8 }}>
                    Contact a super-admin to change your role.
                  </span>
                </div>
              </div>

              <button
                className="apd-btn apd-btn--primary"
                onClick={handleProfileSave}
                disabled={profileSaving}
              >
                {profileSaving ? "Saving…" : "Save Profile"}
              </button>
            </div>
          )}

          {/* PASSWORD TAB */}
          {tab === "password" && (
            <div className="apd-form">
              <div className="apd-form__section-title">Change Password</div>

              {pwError   && <div className="apd-alert apd-alert--error">⚠ {pwError}</div>}
              {pwSuccess && <div className="apd-alert apd-alert--success">✓ {pwSuccess}</div>}

              {[
                { key: "current", label: "Current Password", field: "current_password" },
                { key: "new",     label: "New Password",     field: "new_password" },
                { key: "confirm", label: "Confirm New Password", field: "confirm_password" },
              ].map(({ key, label, field }) => (
                <div className="apd-form__field" key={key}>
                  <label className="apd-form__label">
                    {label} <span className="apd-req">*</span>
                  </label>
                  <div className="apd-pw-wrap">
                    <input
                      className="apd-form__input apd-form__input--pw"
                      type={showPw[key] ? "text" : "password"}
                      value={pwForm[field]}
                      onChange={(e) => setPw(field, e.target.value)}
                      placeholder="••••••••"
                      autoComplete={key === "current" ? "current-password" : "new-password"}
                    />
                    <button
                      type="button"
                      className="apd-pw-toggle"
                      onClick={() => togglePw(key)}
                      tabIndex={-1}
                    >
                      {showPw[key] ? "🙈" : "👁"}
                    </button>
                  </div>
                  {field === "new_password" && (
                    <p className="apd-form__hint">Minimum 8 characters.</p>
                  )}
                </div>
              ))}

              <button
                className="apd-btn apd-btn--primary"
                onClick={handlePasswordSave}
                disabled={pwSaving}
              >
                {pwSaving ? "Changing…" : "Change Password"}
              </button>
            </div>
          )}
        </div>

        {/* ── Footer: Logout ── */}
        <div className="apd-modal__footer">
          <button className="apd-btn apd-btn--logout" onClick={onLogout}>
            <span>⏻</span> Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   LOGOUT CONFIRM MODAL
   ═══════════════════════════════════ */
function LogoutModal({ onConfirm, onCancel }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel]);

  return (
    <div className="apd-modal-overlay" onClick={onCancel}>
      <div className="apd-logout-modal" onClick={(e) => e.stopPropagation()}>
        <div className="apd-logout-modal__icon">⏻</div>
        <h2 className="apd-logout-modal__title">Sign Out?</h2>
        <p className="apd-logout-modal__body">
          You are about to sign out of the admin panel.<br />
          Any unsaved changes will be lost.
        </p>
        <div className="apd-logout-modal__actions">
          <button className="apd-btn apd-btn--cancel" onClick={onCancel}>Cancel</button>
          <button className="apd-btn apd-btn--primary" onClick={onConfirm}>Yes, Sign Out</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   DROPDOWN  (main export)
   ═══════════════════════════════════ */
export default function AdminProfileDropdown({ user, onLogout }) {
  const [dropdownOpen,  setDropdownOpen]  = useState(false);
  const [showProfile,   setShowProfile]   = useState(false);
  const [showLogout,    setShowLogout]    = useState(false);
  const dropRef = useRef(null);

  const initials = (user?.username || "AD").slice(0, 2).toUpperCase();

  /* Close dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target))
        setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const openProfile = () => { setDropdownOpen(false); setShowProfile(true); };
  const openLogout  = () => { setDropdownOpen(false); setShowLogout(true);  };
  const closeAll    = () => { setShowProfile(false); setShowLogout(false);  };

  return (
    <>
      {/* ── Topbar user chip ── */}
      <div className="apd-chip" ref={dropRef}>
        <button
          className={`apd-chip__trigger ${dropdownOpen ? "apd-chip__trigger--open" : ""}`}
          onClick={() => setDropdownOpen((v) => !v)}
          title="Account menu"
        >
          <div className="apd-chip__avatar">{initials}</div>
          <div className="apd-chip__info">
            <span className="apd-chip__name">{user?.username || "Admin"}</span>
            <span className="apd-chip__role">{user?.role || "administrator"}</span>
          </div>
          <span className="apd-chip__chevron">{dropdownOpen ? "▴" : "▾"}</span>
        </button>

        {/* ── Dropdown ── */}
        {dropdownOpen && (
          <div className="apd-dropdown">
            <div className="apd-dropdown__head">
              <div className="apd-dropdown__avatar">{initials}</div>
              <div>
                <div className="apd-dropdown__dname">{user?.username || "Admin"}</div>
                <div className="apd-dropdown__demail">{user?.email || "No email set"}</div>
              </div>
            </div>

            <div className="apd-dropdown__divider" />

            <button className="apd-dropdown__item" onClick={openProfile}>
              <span className="apd-dropdown__item-icon">◈</span>
              <span>My Profile</span>
            </button>
            <button className="apd-dropdown__item" onClick={openProfile}>
              <span className="apd-dropdown__item-icon">🔒</span>
              <span>Change Password</span>
            </button>

            <div className="apd-dropdown__divider" />

            <button className="apd-dropdown__item apd-dropdown__item--danger" onClick={openLogout}>
              <span className="apd-dropdown__item-icon">⏻</span>
              <span>Sign Out</span>
            </button>
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {showProfile && (
        <ProfileModal
          user={user}
          onClose={closeAll}
          onLogout={() => { closeAll(); setShowLogout(true); }}
        />
      )}
      {showLogout && (
        <LogoutModal
          onConfirm={onLogout}
          onCancel={closeAll}
        />
      )}
    </>
  );
}